<template>
  <div class="catalog-page">
    <catalog-category-banner
      class="category-banner"
      v-bind:category="category"
      v-bind:tag="'header'"
    ></catalog-category-banner>
    <breadcrumbs class="catalog-breadcrumbs"></breadcrumbs>
    <section
      class="catalog-wrapper"
      itemscope itemtype="https://schema.org/OfferCatalog"
    >
      <div class="catalog">
        <catalog-jewel-card
          v-for="jewel in jewelsList"
          v-bind:key="jewel.vendor_code_alias"
          v-bind:jewel="jewel"
          itemprop="itemListElement"
        ></catalog-jewel-card>
      </div>
      <catalog-footer
        v-bind:items-showed="jewelsList.length"
        v-bind:items-total="jewelsTotalCount"
        v-bind:page="page"
        v-on:catalog-show-more="updateJewelsListWithNewPage(++page)"
      ></catalog-footer>
    </section>
    <catalog-category-where-to-buy
      class="where-to-buy-banner"
      v-if="category.title"
      v-bind:category-name="category.title"
    ></catalog-category-where-to-buy>
  </div>
</template>

<script>
import Breadcrumbs from '../components/utils/Breadcrumbs'
import CatalogJewelCard from '../components/the-content/CatalogJewelCard'
import CatalogCategoryBanner from '../components/the-content/CatalogCategoryBanner'
import CatalogFooter from '../components/the-content/CatalogFooter'
import CatalogCategoryWhereToBuy from '../components/the-content/CatalogCategoryWhereToBuy'
import utils from './utils'

export default {
  name: 'CatalogCategoryPage',
  components: {
    Breadcrumbs,
    CatalogCategoryBanner,
    CatalogFooter,
    CatalogJewelCard,
    CatalogCategoryWhereToBuy
  },
  data () {
    return {
      category: {},
      jewelsList: [],
      jewelsTotalCount: 0,
      page: (this.$route.params.page)
        ? parseInt(this.$route.params.page) : 1
    }
  },
  metaInfo () {
    return {
      title: this.title,
      titleTemplate: `%s | ${this.titleSuffix}`,
      link: this.linkTags,
      meta: utils.getDefaultMetaTags({
        description: this.metaDescription,
        path: this.$router.resolve(this.$route).href,
        title: this.title,
        imageUrl: this.category.head_image
      })
    }
  },
  mounted () {
    this.getAPIResponse()
  },
  updated () {
    if (this.jewelsList.length > 0 && window['__PRERENDER_INJECTED']) {
      document.dispatchEvent(new Event('render-event'))
    }
    this.$nextTick(function () {
      // restore former pageYOffset value after extra jewels are loaded
      window.scrollTo(0, this.$route.meta.scrollPosition.y)
    })
  },
  watch: {
    $route (to, from) {
      if (to.meta.showMore) {
        // We came here by clicking show more button, do not reload the data
        // meta.showMore will also be used in VueRouter and removed right after
        // Stay at the same scroll position as now
        to.meta.scrollPosition = { x: window.pageXOffset, y: window.pageYOffset }
      } else {
        // after switching to another category, reload the data
        this.resetData()
        this.getAPIResponse()
      }
    }
  },
  computed: {
    title: function () {
      const baseTitle = this.category.title
      const pageSuffix = `, стр. ${this.page}`
      return (this.page > 1) ? baseTitle + pageSuffix : baseTitle
    },
    metaDescription: function () {
      let description = ''
      if (this.category.meta_description) {
        description = this.category.meta_description
        if (this.page > 1) {
          description += ` | Каталог, стр. ${this.page}`
        }
      }
      return description
    },
    linkTags: function () {
      let params = Object.assign({}, this.$route.params)
      let route = {name: 'CatalogCategoryPage', params: params}

      let links = []
      if (this.category.head_image) {
        links.push({rel: 'preload', href: this.category.head_image, as: 'image'})
      }
      if (this.page > 1) {
        route.params = Object.assign(route.params, {page: this.page - 1})
        links.push(this.getRelLink('prev', route))
      }
      route.params = Object.assign(route.params, {page: this.page})
      links.push(this.getRelLink('canonical', route))

      if (this.jewelsList.length < this.jewelsTotalCount) {
        route.params = Object.assign(route.params, {page: this.page + 1})
        links.push(this.getRelLink('next', route))
      }
      return links
    },
    areAnyJewelsNotLoaded: function () {
      return (this.jewelsList.length < this.jewelsTotalCount)
    },
    minJewelPrice: function () {
      // take jewels with non-zero prices
      const jewels = this.jewelsList.filter(x => x.price > 0)
      const prices = jewels.map(x => x.price.toFixed(2))
      return (prices.length > 0) ? Math.min.apply(null, prices) : 0
    },
    titleSuffix: function () {
      return (this.minJewelPrice > 0)
        ? `Купить от ${this.minJewelPrice} р` : 'Эксклюзивное качество'
    }
  },
  methods: {
    resetData: function () {
      this.category = {}
      this.jewelsList = []
      this.jewelsTotalCount = 0
      this.page = (this.$route.params.page)
        ? parseInt(this.$route.params.page) : 1
    },
    getCategory: function (alias) {
      this.$http
        .get(`${process.env.API_HOST}/api/v1/site-structure/get-category/${alias}`)
        .then(response => { this.category = response.body })
        .catch(error => {
          console.log(error)
          // do not redirect since the next API request for jewels' list
          // will raise the 404 error as well and perform a redirect on its end
        })
    },
    updateJewelsListWithNewPage: function (page) {
      const uri = `${process.env.API_HOST}/api/v1/catalog/get-jewels`
      const uriParams = Object.assign({}, this.$route.params, {'page': page})

      this.$http
        .get(uri, {params: uriParams})
        .then(response => {
          this.jewelsTotalCount = response.body['count']
          if (this.areAnyJewelsNotLoaded) {
            this.jewelsList = this.jewelsList.concat(response.body['results'])
          }
        })
        .catch(error => {
          console.log(error)
          this.$router.replace({name: 'Page404'})
        })
    },
    getJewelsList: function (page) {
      const uri = `${process.env.API_HOST}/api/v1/catalog/get-jewels`
      const uriParams = Object.assign(
        {}, this.$route.params, {'page': 1, 'batch': page})

      this.$http
        .get(uri, {params: uriParams})
        .then(response => {
          this.jewelsTotalCount = response.body['count']
          this.jewelsList = response.body['results']
        })
        .catch(error => {
          console.log(error)
          this.$router.replace({name: 'Page404'})
        })
    },
    getAPIResponse: function () {
      this.getCategory(this.$route.params.category)
      this.getJewelsList(this.page)
    },
    getRelLink: function (rel, route) {
      if (route.params.page === 1) {
        delete route.params.page
      }
      let href = this.$router.resolve(route).href
      return {rel: rel, href: utils.addDomainToPath(href)}
    }
  }
}
</script>

<style scoped lang="scss">
  .catalog-page {
    background-color: $default-background-color;
    padding-top: 0;
    padding-bottom: 2em;

    & > .category-banner {
      @include media (">tablet") { margin-bottom: 2em; };
    }
    & > .catalog-breadcrumbs {
      @include media ("<=tablet") { margin-bottom: 1em; };
    }
    & > .catalog-wrapper {
      margin-bottom: 3em;
      & > .catalog {
        display: flex;
        flex-wrap: wrap;
        @include media (">mobile") { padding: 2%; };
        @include media ("<=mobile") { padding: 0; };
        @media only print {
          display: block;
          padding: 0;
        }
      }
    }
  }
</style>



// WEBPACK FOOTER //
// src/pages/CatalogCategoryPage.vue